import {useCallback, useState} from 'react';
import Menu from '../Menu';
import styles from './index.module.less';
import Button from '@/components/common/Button';
import Popover from '@/components/common/Popover';
import Icon from '@/components/common/Icon';
import {useAuthStore} from '@/store/useAuthStore';
import {useModalStore} from '@/store/useModalStore';
import {openPointsRechargeModal, openMembershipModal} from '@/commerce/modals/openModal';
import {PointsRechargeModalSource} from '@/commerce/modals/PointsRechargeModal/types';
import {usePersonalPoints} from '@/hooks/points/usePersonalPoints';
import {useHomeInfo} from '@/hooks/useHomeInfo';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';
import {USER_MANUAL_URL} from '@/constants';
import SubscriptionOrderModal, {
    SubscriptionOrderTabValue,
    type SubscriptionOrderTab,
} from '@/commerce/modals/SubscriptionOrderModal';
import LevelBadge from '@/commerce/components/LevelBadge';
import {useMemberInfo} from '@/commerce/benefit';
import {useMemberLogExt} from '@/hooks/memberLogExt';
import {formatUnixDate} from '@/utils/date';
import {MemberLevelValue} from '@/services/commerce/type';
import {createCx} from '@/utils/cx';
import {MembershipModalSource} from '@/commerce/modals/MembershipModal/types';
import type {PopoverProps} from '@/components/common/Popover';

const cx = createCx(styles);

const POINTS_ICON_URL =
    'https://now.bdstatic.com/stash/v1/01e9009/mangaflow-fe/9896fde/Integral.png';

const AVATAR_VIP_ICON_URL =
    'https://now.bdstatic.com/stash/v1/01e9009/mangaflow-fe/9896fde/home/avatar_vip.png';

// x 轴 17.5px 沿用浮层原有的水平间距（覆盖 antd 默认的 4px）
// .userPanelMenu 的 gap 4px + 退出登录项 48px + .userPanel 的 padding-bottom 11.5px + border 0.5px = 64px
const CONTACT_POPOVER_ALIGN: PopoverProps['align'] = {offset: [17.5, 64]};

const SideBar: React.FC = () => {
    const isLoggedIn = useAuthStore((s) => s.isLogin);
    const userInfo = useAuthStore((s) => s.userInfo);
    const toggleLoginModal = useModalStore((s) => s.toggleLoginModal);
    const toggleLogoutConfirm = useModalStore((s) => s.toggleLogoutConfirm);
    const [subscriptionModal, setSubscriptionModal] = useState<{
        open: boolean;
        defaultTab: SubscriptionOrderTab;
    }>({open: false, defaultTab: SubscriptionOrderTabValue.SUBSCRIPTION});
    const openSubscriptionModal = useCallback(
        (defaultTab: SubscriptionOrderTab) => setSubscriptionModal({open: true, defaultTab}),
        [],
    );
    const closeSubscriptionModal = useCallback(
        () => setSubscriptionModal((prev) => ({...prev, open: false})),
        [],
    );

    const nickname = userInfo?.displayName;
    const avatar = userInfo?.portrait;

    const {personalTotalRemaining, isLoading} = usePersonalPoints();
    const {qrCode, email, contactQrCode} = useHomeInfo();
    const openPointsModal = useModalStore((s) => s.openPointsModal);
    const {sendLog, sendClickLog, sendShowLog} = useTracking();

    const {memberInfo, isLoading: isMemberLoading} = useMemberInfo();
    const memberLogExt = useMemberLogExt();
    // 是否为付费会员
    const isSubscribed = memberInfo && memberInfo.memberLevel !== MemberLevelValue.FREE;
    // 非会员但有历史订阅记录
    const hasExpiredSubscription =
        memberInfo?.memberLevel === MemberLevelValue.FREE && !!memberInfo.lastSubscriptionLevelName;
    // 旗舰版会员，入口引导充值积分
    const isFlagship = memberInfo?.memberLevel === MemberLevelValue.FLAGSHIP;
    // 头像面板会员按钮文案
    const memberBtnText = isSubscribed ? (isFlagship ? '查看会员' : '升级会员') : '订阅会员';

    const handleMemberEntryClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            sendClickLog({
                value: isFlagship ? TrackEvents.CREDIT_RECHARGE_ENTRY : TrackEvents.MEMBER_ENTRY,
                ext: memberLogExt,
            });
            if (isFlagship) {
                openPointsRechargeModal(PointsRechargeModalSource.HOME_SIDEBAR_RECHARGE_POINTS_BTN);
            } else {
                openMembershipModal(MembershipModalSource.HOME_SIDEBAR_MEMBERSHIP_BTN);
            }
        },
        [isFlagship, memberLogExt, sendClickLog],
    );

    const handleAvatarPanelRechargeClick = useCallback(() => {
        sendClickLog({
            value: TrackEvents.USERINFO_CREDIT_RECHARGE,
            ext: memberLogExt,
        });
        openPointsRechargeModal(
            PointsRechargeModalSource.HOME_PERSONAL_AVATAR_PANEL_PURCHASE_POINTS_BTN,
        );
    }, [memberLogExt, sendClickLog]);

    return (
        <aside className={styles.sidebar}>
            {/* 中间路由 */}
            <Menu />

            {/* ===== 底部区域 ===== */}
            <div className={styles.bottom}>
                {/* 已登录：积分 + 头像 */}
                {isLoggedIn && (
                    <>
                        <div className={styles.creditsBox}>
                            <div
                                className={styles.credits}
                                onClick={() => {
                                    sendClickLog({
                                        value: TrackEvents.CREDIT_TAB,
                                        ext: memberLogExt,
                                    });
                                    openPointsModal({type: 'personal'});
                                }}
                            >
                                <img className={styles.creditsIcon} src={POINTS_ICON_URL} alt="" />
                                <span className={styles.creditsNum}>
                                    {isLoading ? '加载中...' : personalTotalRemaining}
                                </span>
                            </div>
                            <div className={styles.divider}></div>
                            {!isMemberLoading && (
                                <div className={styles.member} onClick={handleMemberEntryClick}>
                                    {!memberInfo || memberInfo.memberLevel === MemberLevelValue.FREE
                                        ? '开通会员'
                                        : isFlagship
                                          ? '充值积分'
                                          : '升级会员'}
                                </div>
                            )}
                        </div>

                        <div
                            className={styles.avatarTrigger}
                            onMouseEnter={() => {
                                sendLog({
                                    type: 'hover',
                                    value: TrackEvents.USER_AVATAR,
                                    ext: memberLogExt,
                                });
                                sendShowLog({value: TrackEvents.USER_INFO, ext: memberLogExt});
                            }}
                        >
                            {avatar ? (
                                <img src={avatar} alt={nickname} className={styles.avatarImg} />
                            ) : (
                                <div className={cx('avatarImg', 'avatarPlaceholder')} />
                            )}

                            {isSubscribed && (
                                <img
                                    className={styles.avatarVip}
                                    src={AVATAR_VIP_ICON_URL}
                                    alt="会员标识"
                                />
                            )}

                            <div className={styles.userPanelWrap}>
                                <div
                                    className={styles.userPanel}
                                    data-level={isSubscribed ? memberInfo.memberLevel : undefined}
                                >
                                    <div className={styles.infoBox}>
                                        {avatar ? (
                                            <img
                                                src={avatar}
                                                alt={nickname}
                                                className={styles.userPanelAvatar}
                                            />
                                        ) : (
                                            <div
                                                className={cx(
                                                    'userPanelAvatar',
                                                    'userPanelAvatarPlaceholder',
                                                )}
                                            />
                                        )}
                                        <div
                                            className={`${styles.info} ${isSubscribed || hasExpiredSubscription ? styles.infoWithMeta : ''}`}
                                        >
                                            <div className={styles.name}>{nickname ?? '--'}</div>
                                            {isSubscribed && (
                                                <div className={styles.meta}>
                                                    <LevelBadge memberInfo={memberInfo} />
                                                    {memberInfo.endTime && (
                                                        <span className={styles.expire}>
                                                            {formatUnixDate(
                                                                memberInfo.endTime,
                                                                'YYYY.MM.DD',
                                                            )}
                                                            到期
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {hasExpiredSubscription && (
                                                <div className={styles.meta}>
                                                    <span className={styles.expired}>
                                                        {`${memberInfo.lastSubscriptionLevelName} ${memberInfo.lastSubscriptionEndTime ? formatUnixDate(memberInfo.lastSubscriptionEndTime, 'YYYY.MM.DD') : ''} 已过期`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className={styles.points}
                                        onClick={() => {
                                            sendClickLog({
                                                value: TrackEvents.USERINFO_CREDIT_DETAIL,
                                                ext: memberLogExt,
                                            });
                                            openPointsModal({type: 'personal'});
                                        }}
                                    >
                                        <div className={styles.left}>
                                            <span className={styles.text}>剩余积分</span>
                                            <img
                                                className={styles.pointsIcon}
                                                src={POINTS_ICON_URL}
                                                alt="积分图标"
                                            />
                                            <span className={styles.pointsNum}>
                                                {personalTotalRemaining}
                                            </span>
                                        </div>
                                        <div className={styles.right}>
                                            <span>明细</span>
                                            <Icon name="AijBasicYou" size={12} />
                                        </div>
                                    </div>

                                    <div className={styles.btns}>
                                        <Button
                                            type="primary"
                                            // eslint-disable-next-line react/jsx-no-bind
                                            onClick={() => {
                                                sendClickLog({
                                                    value: TrackEvents.USERINFO_MEMBER_CENTER,
                                                    ext: memberLogExt,
                                                });
                                                openMembershipModal(
                                                    MembershipModalSource.HOME_PERSONAL_AVATAR_PANEL_MEMBERSHIP_BTN,
                                                );
                                            }}
                                        >
                                            {memberBtnText}
                                        </Button>
                                        <Button onClick={handleAvatarPanelRechargeClick}>
                                            充值积分
                                        </Button>
                                    </div>
                                    <div className={styles.userPanelMenu}>
                                        <div
                                            className={styles.userPanelItem}
                                            onClick={() => {
                                                window.open(
                                                    'https://passport.baidu.com/v6/ucenter',
                                                    '_blank',
                                                );
                                                sendClickLog({
                                                    value: TrackEvents.ACCOUNT_SETTING,
                                                });
                                            }}
                                        >
                                            <Icon name="AijBasicShezhi" size={16} />
                                            <span>账号设置</span>
                                        </div>
                                        <div
                                            className={styles.userPanelItem}
                                            onClick={() => {
                                                sendClickLog({
                                                    value: TrackEvents.USERINFO_SUBSCRIPTION_MANAGE,
                                                });
                                                openSubscriptionModal(
                                                    SubscriptionOrderTabValue.SUBSCRIPTION,
                                                );
                                            }}
                                        >
                                            <Icon name="AijBasicDingyueguanli" size={16} />
                                            <span>订阅管理</span>
                                        </div>
                                        <div
                                            className={styles.userPanelItem}
                                            onClick={() => {
                                                sendClickLog({
                                                    value: TrackEvents.USERINFO_ORDER_RECORD,
                                                });
                                                openSubscriptionModal(
                                                    SubscriptionOrderTabValue.ORDER,
                                                );
                                            }}
                                        >
                                            <Icon name="AijBasicPingtaixieyi" size={16} />
                                            <span>订单记录</span>
                                        </div>
                                        <Popover
                                            trigger="hover"
                                            placement="rightBottom"
                                            align={CONTACT_POPOVER_ALIGN}
                                            content={
                                                <div className={styles.contactContent}>
                                                    <img
                                                        className={styles.contactQr}
                                                        src={contactQrCode}
                                                        alt="联系我们二维码"
                                                    />
                                                    <div className={styles.contactTip}>
                                                        微信扫一扫，添加企业微信
                                                    </div>
                                                </div>
                                            }
                                            classNames={{container: styles.popoverContainer}}
                                        >
                                            <div
                                                className={styles.userPanelItem}
                                                onMouseEnter={() => {
                                                    sendLog({
                                                        type: 'hover',
                                                        value: TrackEvents.USERINFO_CONTACT,
                                                    });
                                                }}
                                            >
                                                <Icon name="AijBasicQiyeweixin" size={16} />
                                                <span>联系我们</span>
                                            </div>
                                        </Popover>
                                        <div
                                            className={styles.userPanelItem}
                                            onClick={() => {
                                                sendClickLog({value: TrackEvents.LOGOUT});
                                                toggleLogoutConfirm(true);
                                            }}
                                        >
                                            <Icon name="AijBasicTuichu" size={16} />
                                            <span>退出登录</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {!isLoggedIn && (
                    <div className={styles.loginBtn} onClick={() => toggleLoginModal(true)}>
                        <span className={styles.loginText}>登录</span>
                    </div>
                )}

                <div
                    className={styles.wechatTrigger}
                    onMouseEnter={() => {
                        sendShowLog({value: TrackEvents.WECOME});
                    }}
                >
                    <div className={styles.bottomIcon}>
                        <Icon name="AijBasicQiyeweixin" size={20} />
                    </div>

                    <div className={styles.wechatPanel}>
                        <img className={styles.wechatQr} src={qrCode || ''} alt="企业微信二维码" />
                        <div className={styles.wechatTip}>加入讨论群，了解最新动态</div>
                    </div>
                </div>

                <div
                    className={styles.protocolTrigger}
                    onMouseEnter={() => {
                        sendLog({type: 'hover', value: TrackEvents.PROTOCOL_MENU});
                    }}
                >
                    <div className={styles.bottomIcon}>
                        <Icon name="AijBasicCebianlan" size={20} />
                    </div>

                    <div className={styles.protocolPanel}>
                        <a
                            className={styles.protocolItem}
                            href={USER_MANUAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Icon name="AijBasicJubenxiangqing" size={16} />
                            <span>用户手册</span>
                        </a>
                        <a
                            className={styles.protocolItem}
                            href="/platformAgreement"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                sendClickLog({
                                    value: TrackEvents.PLATFORM_PROTOCOL,
                                });
                            }}
                        >
                            <Icon name="AijBasicPingtaixieyi" size={16} />
                            <span>平台协议</span>
                        </a>
                        <a
                            className={styles.protocolItem}
                            href="/privacyPolicy"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                sendClickLog({
                                    value: TrackEvents.PRIVACY_PROTOCOL,
                                });
                            }}
                        >
                            <Icon name="AijBasicYinsixieyi" size={16} />
                            <span>隐私协议</span>
                        </a>
                        <a
                            className={styles.protocolItem}
                            href="/membershipAgreement"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Icon name="AijBasicJineng" size={16} />
                            <span>会员与积分协议</span>
                        </a>
                        <div className={styles.protocolFooter}>
                            <p className={styles.protocolDisclaimer}>
                                请您合法合规使用平台，并自行对AI生成内容进行判断后使用。
                            </p>
                            <p className={styles.protocolContact}>
                                举报与反馈邮箱
                                <br />
                                {email}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <SubscriptionOrderModal
                open={subscriptionModal.open}
                onClose={closeSubscriptionModal}
                defaultTab={subscriptionModal.defaultTab}
            />
        </aside>
    );
};

export default SideBar;
